import { NgModule } from '@angular/core';
import { RouterModule, Routes } from '@angular/router';
import { defaultRouteGuard } from '@card-software/library';
import { InvoiceDetailComponent } from './components/invoices/invoice-detail/invoice-detail.component';
import { InvoicesComponent } from './components/invoices/invoices/invoices.component';
import { ReceiptsComponent } from './components/receipts/receipts/receipts.component';
import { ReceiptDetailComponent } from './components/receipts/receipt-detail/receipt-detail.component';
import { LoginComponent } from '@card-software/library';
import { PurchaseOrderDetailsComponent } from './components/purchase-orders/purchase-order-details/purchase-order-details.component';
import { PurchaseOrderComponent } from './components/purchase-orders/purchase-order/purchase-order/purchase-order.component';
import { ShipmentDetailsComponent } from './components/shipment/shipment-details/shipment-details.component';
import { ShipmentsComponent } from './components/shipment/shipments/shipments.component';
import { AuthenticationGuard } from './guards/authentication.guard';
import { WorkOrderDetailsComponent } from './components/work-order/work-order-details/work-order-details.component';
import { WorkOrdersComponent } from './components/work-order/work-orders/work-orders.component';
import { UnauthorizedComponent } from './components/unauthorized/unauthorized.component';
import { RoleGuard } from './guards/role.guard';
import {
  ClientPurchaseOrderRoles,
  DeliveryNoteRoles,
  ExternalRequisitionAdminGroup,
  ExternalRequisitionViewerGroup,
  IMEXAdminGroup,
  IMEXEditorGroup,
  IMEXViewerGroup,
  OrderReportingRoles,
  PurchaseOrderRoles,
  WorkOrderRoles,
} from './models/roles/roles-group';
import { CustomerOrderDispatchReportComponent } from './components/reports/customer-order-dispatch-report/customer-order-dispatch-report.component';
import { ClientPurchaseOrdersComponent } from './components/client-purchase-orders/client-purchase-orders.component';
import { ClientPurchaseOrderDetailsComponent } from './components/client-purchase-orders/client-purchase-order-details/client-purchase-order-details.component';
import { RequisitionsComponent } from './components/requisition/requisitions/requisitions.component';
import { RequisitionDetailsComponent } from './components/requisition/requisition-details/requisition-details.component';
import { DeliveryNotesComponent } from './components/delivery-notes/delivery-notes/delivery-notes.component';
import { DeliveryNoteDetailsComponent } from './components/delivery-notes/delivery-note-details/delivery-note-details.component';
import { MENU_ITEMS } from './app-menu';
const routes: Routes = [
  {
    path: '',
    pathMatch: 'full',
    canActivate: [AuthenticationGuard, defaultRouteGuard(MENU_ITEMS, 'Order')],
    component: UnauthorizedComponent,
  },
  {
    path: 'work-orders',
    component: WorkOrdersComponent,
    canActivate: [AuthenticationGuard, RoleGuard],
    data: {
      breadcrumb: [{ label: 'Work_Orders_Title' }],
      roles: [WorkOrderRoles.WorkOrderViewer, WorkOrderRoles.WorkOrderEditor, WorkOrderRoles.WorkOrderAdmin],
    },
  },
  {
    path: 'work-order-details',
    component: WorkOrderDetailsComponent,
    canActivate: [AuthenticationGuard, RoleGuard],
    data: {
      breadcrumb: [
        { label: 'Work_Orders_Title', route: '/work-orders' },
        { label: 'Work_Order_Details_Title', awaitCurrent: true },
      ],
      roles: [WorkOrderRoles.WorkOrderViewer, WorkOrderRoles.WorkOrderEditor, WorkOrderRoles.WorkOrderAdmin],
    },
  },
  {
    path: 'purchase-orders',
    component: PurchaseOrderComponent,
    canActivate: [AuthenticationGuard, RoleGuard],
    data: {
      breadcrumb: [{ label: 'Purchase_Order_Title' }],
      roles: [PurchaseOrderRoles.PurchaseOrderViewer, PurchaseOrderRoles.PurchaseOrderEditor, PurchaseOrderRoles.PurchaseOrderAdmin],
    },
  },
  {
    path: 'purchase-order-details',
    component: PurchaseOrderDetailsComponent,
    canActivate: [AuthenticationGuard, RoleGuard],
    data: {
      breadcrumb: [
        { label: 'Purchase_Order_Title', route: '/purchase-orders' },
        { label: 'Purchase_Order_Details_Title', awaitCurrent: true },
      ],
      roles: [PurchaseOrderRoles.PurchaseOrderViewer, PurchaseOrderRoles.PurchaseOrderEditor, PurchaseOrderRoles.PurchaseOrderAdmin],
    },
  },
  {
    path: 'invoices',
    component: InvoicesComponent,
    canActivate: [AuthenticationGuard, RoleGuard],
    data: {
      breadcrumb: [{ label: 'Invoices_Title' }],
      roles: [IMEXViewerGroup.InvoiceViewer, IMEXAdminGroup.InvoiceAdmin],
    },
  },
  {
    path: 'invoice-detail',
    component: InvoiceDetailComponent,
    canActivate: [AuthenticationGuard, RoleGuard],
    data: {
      breadcrumb: [
        { label: 'Invoices_Title', route: '/invoices' },
        { label: 'Invoice_Details_Title', awaitCurrent: true },
      ],
      roles: [IMEXViewerGroup.InvoiceViewer, IMEXAdminGroup.InvoiceAdmin],
    },
  },
  {
    path: 'receipts',
    component: ReceiptsComponent,
    canActivate: [AuthenticationGuard, RoleGuard],
    data: {
      breadcrumb: [{ label: 'Receipts_Title' }],
      roles: [IMEXViewerGroup.InvoiceViewer, IMEXAdminGroup.InvoiceAdmin],
    },
  },
  {
    path: 'receipt-detail',
    component: ReceiptDetailComponent,
    canActivate: [AuthenticationGuard, RoleGuard],
    data: {
      breadcrumb: [
        { label: 'Receipts_Title', route: '/receipts' },
        { label: 'Receipt_Details_Title', awaitCurrent: true },
      ],
      roles: [IMEXViewerGroup.InvoiceViewer, IMEXAdminGroup.InvoiceAdmin],
    },
  },
  {
    path: 'delivery-notes',
    component: DeliveryNotesComponent,
    canActivate: [AuthenticationGuard, RoleGuard],
    data: {
      breadcrumb: [{ label: 'DeliveryNotes_Title' }],
      roles: [DeliveryNoteRoles.DeliveryNoteViewer, DeliveryNoteRoles.DeliveryNoteAdmin],
    },
  },
  {
    path: 'delivery-note-detail',
    component: DeliveryNoteDetailsComponent,
    canActivate: [AuthenticationGuard, RoleGuard],
    data: {
      breadcrumb: [
        { label: 'DeliveryNotes_Title', route: '/delivery-notes' },
        { label: 'Delivery_Note_Details_Title', awaitCurrent: true },
      ],
      roles: [DeliveryNoteRoles.DeliveryNoteViewer, DeliveryNoteRoles.DeliveryNoteAdmin],
    },
  },
  {
    path: 'shipments',
    component: ShipmentsComponent,
    canActivate: [AuthenticationGuard, RoleGuard],
    data: {
      breadcrumb: [{ label: 'Shipments_Title' }],
      roles: [
        IMEXViewerGroup.ShipmentViewer,
        IMEXEditorGroup.ShipmentEditor,
        IMEXAdminGroup.ShipmentAdmin,
        IMEXAdminGroup.ShipmentSupervisor,
      ],
    },
  },
  {
    path: 'shipment-detail',
    component: ShipmentDetailsComponent,
    canActivate: [AuthenticationGuard, RoleGuard],
    data: {
      breadcrumb: [
        { label: 'Shipments_Title', route: '/shipments' },
        { label: 'Shipment_Details_Title', awaitCurrent: true },
      ],
      roles: [
        IMEXViewerGroup.ShipmentViewer,
        IMEXEditorGroup.ShipmentEditor,
        IMEXAdminGroup.ShipmentAdmin,
        IMEXAdminGroup.ShipmentSupervisor,
      ],
    },
  },
  {
    path: 'requisitions',
    component: RequisitionsComponent,
    canActivate: [AuthenticationGuard, RoleGuard],
    data: {
      breadcrumb: [{ label: 'Requisition_Title' }],
      roles: [ExternalRequisitionAdminGroup.ExternalRequisitionAdmin, ExternalRequisitionViewerGroup.ExternalRequisitionViewer],
    },
  },
  {
    path: 'requisition-details',
    component: RequisitionDetailsComponent,
    canActivate: [AuthenticationGuard, RoleGuard],
    data: {
      breadcrumb: [
        { label: 'Requisition_Title', route: '/requisitions' },
        { label: 'Requisition_Details_Title', awaitCurrent: true },
      ],
      roles: [ExternalRequisitionAdminGroup.ExternalRequisitionAdmin, ExternalRequisitionViewerGroup.ExternalRequisitionViewer],
    },
  },
  {
    path: 'client-purchase-orders',
    component: ClientPurchaseOrdersComponent,
    canActivate: [AuthenticationGuard, RoleGuard],
    data: {
      breadcrumb: [{ label: 'Client_PO_Title' }],
      roles: [
        ClientPurchaseOrderRoles.ClientPurchaseOrderViewer,
        ClientPurchaseOrderRoles.ClientPurchaseOrderEditor,
        ClientPurchaseOrderRoles.ClientPurchaseOrderAdmin,
      ],
    },
  },
  {
    path: 'client-purchase-order-details',
    component: ClientPurchaseOrderDetailsComponent,
    canActivate: [AuthenticationGuard, RoleGuard],
    data: {
      breadcrumb: [
        { label: 'Client_PO_Title', route: '/client-purchase-orders' },
        { label: 'Client_PO_Details_Title', awaitCurrent: true },
      ],
      roles: [
        ClientPurchaseOrderRoles.ClientPurchaseOrderViewer,
        ClientPurchaseOrderRoles.ClientPurchaseOrderEditor,
        ClientPurchaseOrderRoles.ClientPurchaseOrderAdmin,
      ],
    },
  },
  {
    path: 'order-reports',
    component: CustomerOrderDispatchReportComponent,
    canActivate: [AuthenticationGuard, RoleGuard],
    data: {
      breadcrumb: [{ label: 'CustomerOrderReport_Page_Title' }],
      roles: [OrderReportingRoles.OrderReportingViewer, OrderReportingRoles.OrderReportingAdmin],
    },
  },
  {
    path: 'login',
    component: LoginComponent,
  },
  {
    path: 'unauthorized',
    component: UnauthorizedComponent,
  },
  {
    path: '**',
    redirectTo: '',
  },
];

@NgModule({
  imports: [RouterModule.forRoot(routes)],
  exports: [RouterModule],
})
export class AppRoutingModule {}
